import { registerForBootCompleted } from 'owa-plt-data';

type SettledStatus = 'settled' | 'interrupted-by-user' | 'hidden' | 'timeout' | 'unsupported';
type MeasurementMode = 'long-animation-frame' | 'longtask' | 'unsupported';

export interface TimeToSettledResult {
    quietWindowMs: number;
    status: SettledStatus;
    measurementMode: MeasurementMode;
    layoutShiftSupported: boolean;
    observedAt: number;
    bootCompleted?: number;
    settledAt?: number;
    confirmedAt?: number;
}

const quietWindows = [2_000, 5_000];
const observationTimeoutMs = 60_000;
const inputEvents = ['pointerdown', 'keydown', 'click', 'wheel', 'touchstart'];
const results: TimeToSettledResult[] = [];
const callbacks = new Set<(result: TimeToSettledResult) => void>();
let started = false;

export function registerForTimeToSettled(callback: (result: TimeToSettledResult) => void): void {
    for (const result of results) {
        callback(result);
    }
    if (results.length < quietWindows.length) {
        callbacks.add(callback);
    }
}

/**
 * Observes the first two- and five-second quiet windows after the existing boot-ready signal.
 * settledAt is relative to the document time origin; confirmedAt includes the quiet-window wait.
 * This is a custom startup metric, not LCP or CLS. It neither gates work nor counts background
 * requests: critical startup work must participate in the promises preceding boot completion.
 * Each window reports once, including censored observations, within 60 seconds of boot completion.
 */
export function trackTimeToSettled(): void {
    if (started) {
        return;
    }
    started = true;

    registerForBootCompleted(startObservation);
}

function startObservation(bootCompleted: number): void {
    const observationStarted = performance.now();
    const supportedTypes =
        typeof PerformanceObserver === 'undefined'
            ? []
            : (PerformanceObserver.supportedEntryTypes ?? []);
    const measurementMode: MeasurementMode = supportedTypes.includes('long-animation-frame')
        ? 'long-animation-frame'
        : supportedTypes.includes('longtask')
          ? 'longtask'
          : 'unsupported';
    const layoutShiftSupported = supportedTypes.includes('layout-shift');
    const observers: PerformanceObserver[] = [];
    const pendingWindows = new Set(quietWindows);
    let quietStart = Math.max(observationStarted, bootCompleted);
    let quietTimer: number | undefined;
    let timeoutTimer: number | undefined = undefined;

    function cleanup() {
        self.clearTimeout(quietTimer);
        self.clearTimeout(timeoutTimer);
        for (const observer of observers) {
            observer.disconnect();
        }
        document.removeEventListener('visibilitychange', onVisibilityChange);
        self.removeEventListener('pagehide', onPageHide);
        for (const eventName of inputEvents) {
            self.removeEventListener(eventName, onInput, true);
        }
        callbacks.clear();
    }

    function report(quietWindowMs: number, status: SettledStatus, observedAt: number) {
        pendingWindows.delete(quietWindowMs);
        const result: TimeToSettledResult = {
            quietWindowMs,
            status,
            measurementMode,
            layoutShiftSupported,
            observedAt,
            bootCompleted,
            ...(status === 'settled' ? { settledAt: quietStart, confirmedAt: observedAt } : {}),
        };
        results.push(result);
        for (const callback of callbacks) {
            callback(result);
        }
    }

    function stop(status: Exclude<SettledStatus, 'settled'>) {
        const now = performance.now();
        for (const quietWindowMs of pendingWindows) {
            report(quietWindowMs, status, now);
        }
        cleanup();
    }

    function onInput(event: Event) {
        if (event.isTrusted) {
            stop('interrupted-by-user');
        }
    }

    function onVisibilityChange() {
        if (document.visibilityState !== 'visible') {
            stop('hidden');
        }
    }

    function onPageHide() {
        stop('hidden');
    }

    function reportQuietWindows(quietEnd: number, observedAt: number) {
        const boundedQuietEnd = Math.min(quietEnd, bootCompleted + observationTimeoutMs);
        for (const quietWindowMs of pendingWindows) {
            if (boundedQuietEnd - quietStart >= quietWindowMs) {
                report(quietWindowMs, 'settled', observedAt);
            }
        }
    }

    function processEntries(entries: PerformanceEntryList, observedAt: number) {
        for (const entry of entries.sort((a, b) => a.startTime - b.startTime)) {
            if (entry.entryType === 'first-input') {
                if (entry.startTime >= bootCompleted) {
                    reportQuietWindows(entry.startTime, observedAt);
                    stop('interrupted-by-user');
                    return;
                }
                continue;
            }
            if (entry.entryType === 'layout-shift') {
                const shift = entry as PerformanceLayoutShiftEntry;
                if (shift.hadRecentInput || shift.value <= 0) {
                    continue;
                }
            }
            // Preserve a qualifying gap before later activity resets its starting point.
            reportQuietWindows(entry.startTime, observedAt);
            quietStart = Math.max(
                quietStart,
                entry.startTime + (entry.entryType === 'layout-shift' ? 0 : entry.duration)
            );
        }
    }

    function checkQuietWindow(entries: PerformanceEntryList = []) {
        self.clearTimeout(quietTimer);
        // Observer delivery can lag behind the timer. Consume queued activity before confirming.
        for (const observer of observers) {
            entries.push(...observer.takeRecords());
        }
        const now = performance.now();
        processEntries(entries, now);
        if (pendingWindows.size === 0) {
            cleanup();
            return;
        }
        if (document.visibilityState !== 'visible') {
            stop('hidden');
            return;
        }
        if (now >= bootCompleted + observationTimeoutMs) {
            stop('timeout');
            return;
        }
        reportQuietWindows(now, now);
        if (pendingWindows.size === 0) {
            cleanup();
        } else {
            quietTimer = self.setTimeout(
                checkQuietWindow,
                Math.max(0, quietStart + Math.min(...pendingWindows) - now)
            );
        }
    }

    function observe(type: string) {
        const observer = new PerformanceObserver(list => {
            checkQuietWindow(list.getEntries());
        });
        observers.push(observer);
        observer.observe({ type, buffered: true });
    }

    if (document.visibilityState !== 'visible') {
        stop('hidden');
        return;
    }
    if (measurementMode === 'unsupported') {
        stop('unsupported');
        return;
    }

    document.addEventListener('visibilitychange', onVisibilityChange);
    self.addEventListener('pagehide', onPageHide);
    for (const eventName of inputEvents) {
        self.addEventListener(eventName, onInput, { capture: true, passive: true });
    }
    observe(measurementMode);
    if (layoutShiftSupported) {
        observe('layout-shift');
    }
    if (supportedTypes.includes('first-input')) {
        observe('first-input');
    }
    timeoutTimer = self.setTimeout(
        () => stop('timeout'),
        Math.max(0, bootCompleted + observationTimeoutMs - performance.now())
    );
    checkQuietWindow();
}
